import React, { useState } from "react";
import { Button } from "@src/lib/ui/Button";
import { Card, CardContent } from "@src/lib/ui/Card";
import Text from "@src/lib/ui/Text";
import ArrowUploadIcon from "@src/lib/icons/18px/arrow-upload";
import MessagePenIcon from "@src/lib/icons/18px/message-pen";
import AddGuestFeedbackIntegrationButton from "./AddGuestFeedbackIntegrationButton";
import GuestFeedbackUploadDialog from "./GuestFeedbackUploadBatchDialog";
import Spinner from "@src/lib/ui/Spinner";
import { useGuestFeedbackCsvImport } from "./useGuestFeedbackCsvImport";
import GuestFeedbackUploadBatches, {
  UPLOAD_BATCHES_PAGE_SIZE,
} from "./GuestFeedbackUploadBatches";
import { useGuestFeedbackUploadBatchesQuery } from "./GuestFeedbackUploadBatches.generated";
import { GuestFeedbackUploadBatchStatus } from "@src/types.generated";

const GUEST_FEEDBACK_HELP_LINK =
  "https://help.opus.so/en/articles/15915045-guest-feedback-connecting-data-sources-reviewing-your-feedback";

const GuestFeedbackGettingStarted: React.FC = () => {
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const { isCsvImportInProgress: importWindowOpen } =
    useGuestFeedbackCsvImport();
  // The status panel above the card reports each batch upload, including a
  // first upload that is importing or needs attention. Read its (cached)
  // batches so the card shows its own import spinner only when the panel has
  // nothing to say, e.g. right after submitting, before the batch loads.
  const { data: batchesData } = useGuestFeedbackUploadBatchesQuery({
    variables: { limit: UPLOAD_BATCHES_PAGE_SIZE },
    fetchPolicy: "cache-only",
  });
  const panelShowsUpload =
    !!batchesData?.GuestFeedbackUploadBatches.objects.some(
      (batch) =>
        batch.status === GuestFeedbackUploadBatchStatus.Processing ||
        batch.status === GuestFeedbackUploadBatchStatus.NeedsAttention,
    );
  const isCsvImportInProgress = importWindowOpen && !panelShowsUpload;

  return (
    <div className="flex w-full flex-col items-center gap-4 py-8">
      <div className="w-full max-w-[560px] empty:hidden">
        <GuestFeedbackUploadBatches />
      </div>
      <Card variant="elevated" className="w-full max-w-[560px]">
        <CardContent className="flex flex-col items-center gap-6 p-10">
          <div className="flex flex-col items-center gap-3">
            <span className="flex size-12 items-center justify-center rounded-full bg-bg-secondary text-icon-secondary [&_svg]:size-6">
              {isCsvImportInProgress ? (
                <Spinner size={24} />
              ) : (
                <MessagePenIcon />
              )}
            </span>
            <Text variant="page-title" className="text-center">
              {isCsvImportInProgress
                ? "Importing your reviews"
                : "Connect your guest feedback"}
            </Text>
            <Text
              variant="body-description"
              multiline
              className="max-w-[440px] text-center text-text-secondary"
            >
              {isCsvImportInProgress
                ? "Opus is reading your file. Your reviews will appear here automatically once the import finishes — this usually takes a minute."
                : "Opus reads your reviews, groups them into themes, and turns the recurring issues into prioritized training recommendations."}
            </Text>
          </div>

          {!isCsvImportInProgress && (
            <div className="flex flex-wrap items-center justify-center gap-2">
              <AddGuestFeedbackIntegrationButton />
              <Button
                variant="outline"
                size="md"
                onClick={() => setUploadDialogOpen(true)}
              >
                <ArrowUploadIcon />
                Upload CSV
              </Button>
            </div>
          )}

          <Text
            variant="fine-print-description"
            multiline
            className="text-center text-text-tertiary"
          >
            Connected sources sync daily and Opus analyzes feedback weekly.{" "}
            <a
              href={GUEST_FEEDBACK_HELP_LINK}
              target="_blank"
              rel="noreferrer"
              className="text-text-secondary underline"
            >
              Learn more
            </a>
          </Text>
        </CardContent>
      </Card>
      <GuestFeedbackUploadDialog
        open={uploadDialogOpen}
        onOpenChange={setUploadDialogOpen}
      />
    </div>
  );
};

export default GuestFeedbackGettingStarted;
